declare global {
    interface Window {
        electronAPI: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            saveFile: (arg: any) => Promise<{
                status: string;
                path?: string;
                message?: string;
            }>;
        };
    }
}

import dayjs from "dayjs";
import {
    code,
    diagnosis,
    diagnosisCode,
    diagnosisInfo,
    encounter,
    updateDiagnosisInfo
} from "dto/encounter.dto";
import favoriteDiagnosis from "dto/favoriteDiagnosis.dto";
import { clinicDTO } from "dto/his/clinicDTO";
import { visitData } from "dto/his/visitData";
import useApiRequest from "hooks/useApiRequest";
import useBackdrop from "hooks/useBackdrop";
import useQueryParams from "hooks/useQueryParams";
import { debounce, first, isEmpty, isEqual } from "lodash";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from "react";
import { UseFormReturn, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import diagnosisService from "services/api/diagnosis.service";
import encounterService from "services/api/encounter.service";
import diagnosisCodeService from "services/api/diagnosisCode.service";
import hisService from "services/api/his.service";
import impressService from "services/impress";
import { v4 as uuidv4 } from "uuid";

interface EncounterContext {
    rawEncounterData: encounter | undefined;
    encounterForm: UseFormReturn<encounter>;
    rawIcdCode: diagnosisCode;
    // isIcdCodeChange: boolean;
    allDiagnosis: diagnosis[];
    submitDoctor: (data: encounter) => void;
    submitCoder: (data: encounter) => void;
    getDefaultCoderForm: () => diagnosisCode;
    loading: boolean;
    fetchedEncounter: boolean;
    isOverride: boolean;
    isEditable: boolean;
    recorder: {
        name: string;
        license: string;
    };
    activeTab: "coder" | "doctor";
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChangeTab: (_: any, value: "coder" | "doctor") => void;
    activeUtilTab: "fav-dx" | "re-dx";
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChangeUtilTab: (_: any, value: "fav-dx" | "re-dx") => void;
    insertOrReplaceDiagnosis: (
        name:
            | "principal"
            | "comorbidity"
            | "complication"
            | "other"
            | "externalCause",
        item: favoriteDiagnosis
    ) => void;
    removeDiagnosis: (
        name:
            | "principal"
            | "comorbidity"
            | "complication"
            | "other"
            | "externalCause",
        item: favoriteDiagnosis
    ) => void;
    clinicData: clinicDTO[];
    setDefaultOverwriteIcd10: () => void;
    service: string;
    impressApp:
        | {
              version: string;
          }
        | undefined;
    openConfirmCode: boolean;
    handleIsSure: () => void;
    handleCloseCode: (value: boolean) => void;
}

const EncounterContext = createContext<EncounterContext | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useEncounter = () => {
    const context = useContext(EncounterContext);

    if (!context) {
        throw new Error(
            "useEncounter must be used within an EncounterProvider"
        );
    }

    return context;
};

const closeTab = () => {
    console.log("Attempting to close the tab...");
    window.opener = null;
    window.open("", "_self");
    window.close();
    setTimeout(() => {
        console.warn("Please close this tab manually.");
        // alert(
        //     "Web browser policy not allow to close the tab! Please close this tab manually."
        // );
    }, 100);
};

const EncounterProvider = (props: { children: JSX.Element }) => {
    const { children } = props;
    const [loading, setLoading] = useState(true);
    const [fetchedEncounter, setFetchedEncounter] = useState(false);
    const [isOverride] = useState(false);
    const [isEditable, setEditable] = useState(false);
    const [recorder, setRecorder] = useState({
        name: "",
        license: ""
    });

    const [recorderParams] = useQueryParams("recorder");
    const [refreshCode, setReFreshCode] = useState(0);

    // IMPRESS_VERSION
    const [getVersion, impressApp] = useApiRequest(
        impressService.getImpressVersion,
        {
            success: false,
            error: false
        }
    );
    useEffect(() => {
        getVersion();
    }, [getVersion]);

    useEffect(() => {
        const fetchRecorder = async (id: string) => {
            if (!isEmpty(id)) {
                const { data: recorderData } = await hisService.getRecorder(id);
                setRecorder({
                    name: recorderData?.name ?? "",
                    license: id
                });
            }
        };
        fetchRecorder(recorderParams ?? "");
    }, [recorderParams]);

    const [activeTab = "doctor", setActiveTab] = useQueryParams(
        "tab",
        "doctor"
    );

    const [rawEncounterData, setEncounterRawData] = useState<encounter>();
    const [openConfirmCode, setOpenConfirmCode] = useState(false);

    const [activeUtilTab = "re-dx", setActiveUtilTab] = useQueryParams(
        "util",
        "re-dx"
    );

    const handleOnChangeUtilTab = (
        _: React.SyntheticEvent,
        value: "fav-dx" | "re-dx"
    ) => {
        setActiveUtilTab(value);
    };

    const { openBackdrop, closeBackdrop } = useBackdrop();

    const encounterForm = useForm<encounter>({
        defaultValues: {
            hospitalNumber: "",
            transactionNumber: "",
            firstName: "",
            lastName: "",
            service: "",
            serviceName: "",
            diagnosis: {
                principal: [
                    {
                        id: uuidv4(),
                        term: ""
                    }
                ],
                comorbidity: [],
                complication: [],
                other: [],
                externalCause: []
            },
            overwriteIcd10: {
                principal: [],
                comorbidity: [],
                complication: [],
                other: [],
                externalCause: []
            }
        }
    });

    const allDiagnosis = encounterForm.watch([
        "diagnosis.principal",
        "diagnosis.comorbidity",
        "diagnosis.complication",
        "diagnosis.other",
        "diagnosis.externalCause"
    ]);

    const { watch: encounterWatch } = encounterForm;

    const [hospitalNumber, transactionNumber, service] = encounterWatch([
        "hospitalNumber",
        "transactionNumber",
        "service"
    ]);

    // const icdCodeV2Form = useForm<diagnosisCode>({
    //     defaultValues: {
    //         principal: [
    //             {
    //                 icd: [],
    //                 diagnosis: []
    //             }
    //         ],
    //         comorbidity: [],
    //         complication: [],
    //         other: [],
    //         externalCause: [],
    //     }
    // });

    const [hn] = useQueryParams("hn", "");
    const [txn] = useQueryParams("txn", "");
    const [serviceParam] = useQueryParams("service");
    // const [patientFName] = useQueryParams("firstName");
    // const [patientLName] = useQueryParams("lastName");
    // const [sex] = useQueryParams("sex");
    // const [birthday] = useQueryParams("birthday");

    const [, setEncounterData] = useState<encounter>();
    const [diagnosisData, setDiagnosisData] = useState<diagnosisInfo>();
    const [visitData, setVisitData] = useState<visitData>();
    const [clinicData, setClinicData] = useState<clinicDTO[]>([]);
    const { setValue } = encounterForm;

    const serviceName = useMemo(() => {
        const existed = (Array.isArray(clinicData) ? clinicData : []).find(
            (i) => i.clinicId === serviceParam
        );

        return existed?.clinicName ?? "ไม่พบข้อมูล";
    }, [clinicData, serviceParam]);

    const _serviceParams = useMemo(
        () => (serviceParam ?? "").trim(),
        [serviceParam]
    );

    const fetchVisitData = useCallback(async () => {
        const { data: visitData } = await hisService.getVisitData(
            hn,
            txn,
            _serviceParams
        );
        setVisitData(visitData);
    }, [hn, txn, _serviceParams]);

    // GET ICD CODE
    // http://localhost:5173/?hn=1111&txn=2222&recorder=doctor1234&service=opd1234&tab=doctor&util=re-dx
    const [rawIcdCode, setRawIcdCode] = useState<diagnosisCode>();
    useEffect(() => {
        const fetchIcdCode = async () => {
            try {
                const res = await diagnosisCodeService.getDiagnosisCode(
                    transactionNumber,
                    hospitalNumber,
                    service ?? ""
                );
                if (res.status === 200 && res.data) {
                    // setRawIcdCode({
                    //     ...res.data,
                    //     principal: res.data.principal ? [res.data.principal] as unknown as code[] : []
                    // });

                    setRawIcdCode(res.data);
                } else {
                    return null;
                }
            } catch (error) {
                console.error("Error fetching ICD code:", error);
                return null;
            }
        };
        if (hospitalNumber && transactionNumber && service) {
            fetchIcdCode();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transactionNumber, openConfirmCode, refreshCode]);

    const handleIsSure = () => {
        setOpenConfirmCode(false);
        // encounterForm.handleSubmit(submitCoder)();
        setActiveTab("doctor");
    };
    const handleCloseCode = (value: boolean) => {
        setOpenConfirmCode(false);
        setActiveTab("coder");
        setOpenConfirmCode(value);
    };

    useEffect(() => {
        if (visitData?.HN && visitData?.VN) {
            setValue("service", _serviceParams ?? visitData?.CLINICID ?? "");
            setValue("serviceName", serviceName ?? visitData?.DEPTDESC ?? "");
            // setValue("firstName", visitData?.FIRSTNAME ?? patientFName ?? "");
            // setValue("lastName", visitData?.LASTNAME ?? patientLName ?? "");
            // setValue("sex", (sex ?? visitData.sex === "ญ") ? "female" : "male");
            // setValue(
            //     "birthday",
            //     dayjs(visitData.BIRTHDAY).toISOString() ?? birthday
            // );
            setValue("firstName", visitData.FIRSTNAME);
            setValue("lastName", visitData.LASTNAME);
            setValue("sex", visitData.sex === "ญ" ? "female" : "male");
            setValue("doctorId", visitData.DOCTORID);
            setValue("birthday", dayjs(visitData.BIRTHDAY).toISOString());
            setValue(
                "doctorName",
                `${visitData?.DOCTORPREFIX ?? ""} ${visitData?.DOCTORFIRSTNAME ?? ""} ${visitData?.DOCTORLASTNAME ?? ""}`.trim()
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        _serviceParams,
        // birthday,
        // patientFName,
        // patientLName,
        // sex,
        serviceName,
        setValue,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        JSON.stringify(visitData)
    ]);

    const fetchClinics = useCallback(async () => {
        const { data: resClinics } = await hisService.getClinic();

        if (resClinics) {
            setClinicData(resClinics);
        }
    }, []);

    const setParamsValues = useCallback(async () => {
        if (!isEmpty(hn) && !isEmpty(txn) && !isEmpty(_serviceParams)) {
            setEditable(true);
            setValue("hospitalNumber", hn);
            setValue("transactionNumber", txn);
            setValue("service", _serviceParams);
        } else {
            setLoading(false);
        }
    }, [hn, txn, _serviceParams, setValue]);

    const fetchPrefill = useCallback(
        async (hospitalNumber: string) => {
            const { data: prefillData } =
                await encounterService.getPrefill(hospitalNumber);

            const _prefillData = prefillData.map((i) => {
                return {
                    ...i,
                    id: uuidv4()
                };
            });

            encounterForm.setValue("diagnosis.comorbidity", _prefillData);
        },
        [encounterForm]
    );

    const fetchEncounter = useCallback(
        async (
            hospitalNumber: string,
            transactionNumber: string,
            service?: string
        ) => {
            try {
                setLoading(true);
                const { data: encounter } =
                    await encounterService.getEncounterByHNAndTXNAndService(
                        hospitalNumber,
                        transactionNumber,
                        service
                    );

                setEncounterRawData(encounter);

                if (encounter?.id) {
                    const { data: diagnosisInfo } =
                        await diagnosisService.getDiagnosis(encounter.id!);

                    setEncounterData(encounter);
                    setDiagnosisData(diagnosisInfo);
                    encounterForm.reset({
                        ...encounter,
                        service: encounter?.service ?? "",
                        serviceName: encounter?.serviceName ?? "",
                        firstName: encounter?.firstName ?? "",
                        lastName: encounter?.lastName ?? "",
                        sex: encounter?.sex,
                        birthday: encounter?.birthday ?? "",
                        diagnosis: {
                            ...diagnosisInfo?.diagnosis,
                            principal: [
                                diagnosisInfo?.diagnosis?.principal
                            ].filter((i) => i)
                        },
                        overwriteIcd10: {
                            ...diagnosisInfo?.overwrittenIcd10,
                            principal: [
                                diagnosisInfo?.overwrittenIcd10?.principal
                            ].filter((i) => i)
                        }
                    });
                } else {
                    encounterForm.reset({
                        hospitalNumber,
                        transactionNumber,
                        service,
                        firstName: "",
                        lastName: "",
                        diagnosis: {
                            principal: [
                                {
                                    id: uuidv4(),
                                    term: ""
                                }
                            ],
                            comorbidity: [],
                            complication: [],
                            other: [],
                            externalCause: []
                        },
                        overwriteIcd10: {
                            principal: [],
                            comorbidity: [],
                            complication: [],
                            other: [],
                            externalCause: []
                        }
                    });
                    fetchPrefill(hospitalNumber).then(() => {
                        console.info("fetch prefill");
                    });
                    return null;
                }
                return encounter;
            } catch (error) {
                console.error(error);
            } finally {
                fetchVisitData();
                setFetchedEncounter(true);
                setLoading(false);
            }
        },
        [encounterForm, fetchPrefill, fetchVisitData]
    );

    const isDiagnosisChnage = () => {
        const diagnosisForm = encounterForm.getValues("diagnosis");
        const convertDiagnosisForm = {
            ...diagnosisForm,
            principal: diagnosisForm.principal[0]
        };
        if (diagnosisData) {
            const areEqual = isEqual(
                convertDiagnosisForm,
                diagnosisData.diagnosis
            );
            if (!areEqual) {
                return true;
            } else {
                return false;
            }
        }
    };

    const debounceFetch = useMemo(() => {
        return debounce(fetchEncounter, 500);
    }, [fetchEncounter]);

    useEffect(() => {
        setParamsValues();
    }, [setParamsValues]);

    useEffect(() => {
        fetchClinics();
    }, [fetchClinics]);

    useEffect(() => {
        if (
            !isEmpty(hospitalNumber) &&
            !isEmpty(transactionNumber) &&
            !isEmpty(service)
        ) {
            debounceFetch(hospitalNumber, transactionNumber, service);
        }
    }, [hospitalNumber, transactionNumber, service, debounceFetch]);

    const handleSaveBinaryMRIS = async () => {
        if (typeof window.electronAPI !== "undefined") {
            // alert("Electron API is available.");
            const parsedVisitDate = dayjs(visitData?.VISITDATE || new Date());
            const visitDate = parsedVisitDate.isValid()
                ? parsedVisitDate.format("YYYYMMDD")
                : typeof visitData?.VISITDATE === "string" &&
                    (visitData.VISITDATE as string)?.length > 0
                  ? (visitData.VISITDATE as string).replace(/[^0-9]/g, "")
                  : dayjs().format("YYYYMMDD");

            await window.electronAPI.saveFile({
                hospitalNumber,
                transactionNumber,
                service,
                recorder:
                    encounterForm.getValues("doctorId") ?? recorder.license,
                visitDate
            });
        } else {
            // alert("Electron API is not available. Function not called.");
            console.warn("Electron API is not available. Function not called.");
        }
    };

    const upsertEncounter = async (data: encounter) => {
        const {
            hospitalNumber,
            transactionNumber,
            service,
            firstName,
            lastName,
            sex,
            serviceName,
            birthday,
            doctorId,
            doctorName
        } = data;
        let id = data.id;
        if (data.id) {
            // * Update Encounter
            const { data: encounter } = await encounterService.updateEncounter(
                data.id!,
                {
                    hospitalNumber,
                    transactionNumber,
                    service,
                    firstName,
                    lastName,
                    sex,
                    serviceName,
                    birthday,
                    doctorId,
                    doctorName
                }
            );
            id = encounter.id;
        } else {
            // * Create Encounter
            const { data: encounter } = await encounterService.createEncounter({
                hospitalNumber,
                transactionNumber,
                service,
                firstName,
                lastName,
                sex,
                serviceName,
                birthday,
                doctorId,
                doctorName
            });

            id = encounter.id;
        }
        return id;
    };

    const submitDoctor = async (data: encounter) => {
        toast.promise(
            async () => {
                try {
                    openBackdrop();
                    const { diagnosis } = data;
                    // * Upsert Diagnosis
                    const id = await upsertEncounter(data);

                    if (id) {
                        const overwriteIcd10 = getDefaultCoderForm();
                        const diagnosisInfo: updateDiagnosisInfo = {
                            deleteDiagnosis: false,
                            deleteOverwrittenIcd10: false,
                            diagnosis: {
                                principal: first(diagnosis.principal)!,
                                comorbidity: diagnosis.comorbidity,
                                complication: diagnosis.complication,
                                other: diagnosis.other,
                                externalCause: diagnosis.externalCause
                            },
                            overwrittenIcd10: {
                                principal: first(overwriteIcd10.principal)!,
                                comorbidity: overwriteIcd10.comorbidity,
                                complication: overwriteIcd10.complication,
                                other: overwriteIcd10.other,
                                externalCause: overwriteIcd10.externalCause
                            }
                        };

                        const { data: result } =
                            await diagnosisService.updateDiagnosis(
                                id!,
                                diagnosisInfo
                            );

                        await handleSaveBinaryMRIS();
                        closeTab();
                        return result;
                    }
                } catch (error) {
                    console.error(error);
                    throw error;
                } finally {
                    fetchEncounter(hospitalNumber, transactionNumber, service);
                    closeBackdrop();
                }
            },
            {
                pending: "Saving Encounter...",
                success: "Encounter Saved!",
                error: "Error Saving Encounter!"
            }
        );
    };

    const submitCoder = async (data: encounter) => {
        toast.promise(
            async () => {
                try {
                    openBackdrop();
                    const { overwriteIcd10: overwriteICD10, diagnosis } = data;
                    // * Upsert Overwrites ICD10
                    const id = await upsertEncounter(data);

                    if (id) {
                        const diagnosisInfo: updateDiagnosisInfo = {
                            deleteDiagnosis: false,
                            deleteOverwrittenIcd10: false,
                            overwrittenIcd10: {
                                principal: first(overwriteICD10.principal)!,
                                comorbidity: overwriteICD10.comorbidity,
                                complication: overwriteICD10.complication,
                                other: overwriteICD10.other,
                                externalCause: overwriteICD10.externalCause
                            },
                            diagnosis: {
                                principal: first(diagnosis.principal)!,
                                comorbidity: diagnosis.comorbidity,
                                complication: diagnosis.complication,
                                other: diagnosis.other,
                                externalCause: diagnosis.externalCause
                            }
                        };

                        const { data: result } =
                            await diagnosisService.updateDiagnosis(
                                id!,
                                diagnosisInfo
                            );

                        await handleSaveBinaryMRIS();
                        closeTab();
                        return result;
                    }
                } catch (error) {
                    console.error(error);
                    throw error;
                } finally {
                    fetchEncounter(hospitalNumber, transactionNumber, service);
                    closeBackdrop();
                    setReFreshCode((pre) => pre + 1);
                }
            },
            {
                pending: "Saving Encounter...",
                success: "Encounter Saved!",
                error: "Error Saving Encounter!"
            }
        );
    };

    const [principal, comorbidity, complication, other, externalCause] =
        allDiagnosis;

    const mergeICDObject = (value: code[], value2: code[]) => {
        const result = [
            ...value,
            ...value2.filter((i) => {
                const isExistedInValue = value.some((j) =>
                    isEqual(
                        (i?.diagnosis ?? []).sort(),
                        (j?.diagnosis ?? []).sort()
                    )
                );

                return !isExistedInValue;
            })
        ];
        return result;
    };

    const isIcdCodeChange = () => {
        const coderForm = encounterForm.watch("overwriteIcd10");
        // console.log("coderForm outside", coderForm);
        // console.log("raw outside", doctorForm);
        if (rawIcdCode) {
            const convertRawCode: diagnosisCode = {
                principal: [rawIcdCode.principal] as unknown as code[],
                comorbidity: rawIcdCode.comorbidity,
                complication: rawIcdCode.complication,
                other: rawIcdCode.other,
                externalCause: rawIcdCode.externalCause
            };
            const convertCodeForm: diagnosisCode = {
                principal: coderForm.principal,
                comorbidity: coderForm.comorbidity,
                complication: coderForm.complication,
                other: coderForm.other,
                externalCause: coderForm.externalCause
            };
            // console.log(convertCodeForm);
            // console.log(convertRawCode);
            return isEqual(convertRawCode, convertCodeForm);
        }
    };

    const handleOnChangeTab = useCallback(
        (_: React.SyntheticEvent, value: "coder" | "doctor") => {
            if (value === "doctor") {
                const isCodeChange = isIcdCodeChange();
                if (!isCodeChange && rawIcdCode) {
                    if (!isDiagnosisChnage()) {
                        setOpenConfirmCode(true);
                    } else {
                        setActiveTab(value);
                    }
                } else {
                    setActiveTab(value);
                }
            } else {
                setActiveTab(value);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setActiveTab]
    );

    const mergeArrays = (arr1: code[], arr2: code[]) => {
        const map = new Map<string, code>();

        [...arr1, ...arr2].forEach((item) => {
            if (item.icd && item.icd.length > 0) {
                // Create a unique key by joining all icd values (sorted for consistency)
                const key = item.icd
                    .map((icdItem) => icdItem.term)
                    .sort()
                    .join("|");
                if (!map.has(key)) {
                    map.set(key, item);
                }
            }
        });

        return Array.from(map.values());
    };

    const getDefaultCoderForm = useCallback((): diagnosisCode => {
        const defaultPrincipal = principal
            .filter((i) => !isEmpty(i.term))
            .map((i) => {
                return {
                    diagnosis: [i.id!],
                    icd: i.icd10
                };
            });

        const defaultComorbidity = comorbidity
            .filter((i) => !isEmpty(i.term))
            .map((i) => {
                return {
                    diagnosis: [i.id!],
                    icd: i.icd10
                };
            });

        const defaultComplication = complication
            .filter((i) => !isEmpty(i.term))
            .map((i) => {
                return {
                    diagnosis: [i.id!],
                    icd: i.icd10
                };
            });

        const defaultOther = other
            .filter((i) => !isEmpty(i.term))
            .map((i) => {
                return {
                    diagnosis: [i.id!],
                    icd: i.icd10
                };
            });

        const defaultExternalCause = externalCause
            .filter((i) => !isEmpty(i.term))
            .map((i) => {
                return {
                    diagnosis: [i.id!],
                    icd: i.icd10
                };
            });

        const allDxId = [
            ...defaultPrincipal.flatMap((i) => i.diagnosis),
            ...defaultComorbidity.flatMap((i) => i.diagnosis),
            ...defaultComplication.flatMap((i) => i.diagnosis),
            ...defaultOther.flatMap((i) => i.diagnosis),
            ...defaultExternalCause.flatMap((i) => i.diagnosis)
        ];

        if (diagnosisData?.overwrittenIcd10) {
            const {
                principal,
                comorbidity,
                complication,
                other,
                externalCause
            } = diagnosisData.overwrittenIcd10;

            // clean not exist dx
            const cleanedPrincipal = [principal]
                .filter((i) =>
                    (i?.diagnosis ?? []).some((j) => allDxId.includes(j))
                )
                .filter((i) =>
                    i.diagnosis.every((i) =>
                        defaultPrincipal.flatMap((i) => i.diagnosis).includes(i)
                    )
                );

            const cleanedComorbidity = comorbidity
                .filter((i) => i.diagnosis.some((j) => allDxId.includes(j)))
                .filter((i) =>
                    i.diagnosis.every((i) =>
                        defaultComorbidity
                            .flatMap((i) => i.diagnosis)
                            .includes(i)
                    )
                );

            const cleanedComplication = complication
                .filter((i) => i.diagnosis.some((j) => allDxId.includes(j)))
                .filter((i) =>
                    i.diagnosis.every((i) =>
                        defaultComplication
                            .flatMap((i) => i.diagnosis)
                            .includes(i)
                    )
                );

            const cleanedOther = other
                .filter((i) => i.diagnosis.some((j) => allDxId.includes(j)))
                .filter((i) =>
                    i.diagnosis.every((i) =>
                        defaultOther.flatMap((i) => i.diagnosis).includes(i)
                    )
                );

            const cleanedExternalCause = externalCause
                .filter((i) => i.diagnosis.some((j) => allDxId.includes(j)))
                .filter((i) =>
                    i.diagnosis.every((i) =>
                        defaultExternalCause
                            .flatMap((i) => i.diagnosis)
                            .includes(i)
                    )
                );

            // Write Function merge here!!

            // const convertRawCode: diagnosisCode = {
            //     principal: rawIcdCode.principal,
            //     comorbidity: rawIcdCode.comorbidity,
            //     complication: rawIcdCode.complication,
            //     other: rawIcdCode.other,
            //     externalCause: rawIcdCode.externalCause
            // };
            // const mergedObjectPrincipal = mergeICDObject(cleanedPrincipal, defaultPrincipal);
            // const mergedObjectComorbidity = mergeICDObject(cleanedComorbidity, defaultComorbidity);
            // const mergedObjectComplication = mergeICDObject(cleanedComplication, defaultComplication);
            // const mergedObjectOther = mergeICDObject(cleanedOther, defaultOther);
            // const mergedObjectExternalCause = mergeICDObject(cleanedExternalCause, defaultExternalCause);

            // const mergeArraysPrinciple = mergeArrays(mergedObjectPrincipal, convertRawCode.principal);
            // const mergeArraysComorbidity = mergeArrays(mergedObjectComorbidity, convertRawCode.comorbidity);
            // const mergeArraysComplication = mergeArrays(mergedObjectComplication, convertRawCode.complication);
            // const mergeArraysOther = mergeArrays(mergedObjectOther, convertRawCode.other);
            // const mergeArraysExternalCause = mergeArrays(mergedObjectExternalCause, convertRawCode.externalCause);

            // return {
            //     principal: mergeArraysPrinciple,
            //     comorbidity: mergeArraysComorbidity,
            //     complication: mergeArraysComplication,
            //     other: mergeArraysOther,
            //     externalCause: mergeArraysExternalCause
            // };

            const processIcdCode = (rawIcdCode?: diagnosisCode) => {
                const mergeFields = (
                    cleaned: code[],
                    defaultVal: code[],
                    raw: code[]
                ) => mergeArrays(mergeICDObject(cleaned, defaultVal), raw);

                if (rawIcdCode) {
                    const convertRawCode = { ...rawIcdCode };
                    return {
                        principal: mergeFields(
                            cleanedPrincipal,
                            defaultPrincipal,
                            [convertRawCode.principal] as unknown as code[]
                        ),
                        comorbidity: mergeFields(
                            cleanedComorbidity,
                            defaultComorbidity,
                            convertRawCode.comorbidity
                        ),
                        complication: mergeFields(
                            cleanedComplication,
                            defaultComplication,
                            convertRawCode.complication
                        ),
                        other: mergeFields(
                            cleanedOther,
                            defaultOther,
                            convertRawCode.other
                        ),
                        externalCause: mergeFields(
                            cleanedExternalCause,
                            defaultExternalCause,
                            convertRawCode.externalCause
                        )
                    };
                }

                return {
                    principal: mergeICDObject(
                        cleanedPrincipal,
                        defaultPrincipal
                    ),
                    comorbidity: mergeICDObject(
                        cleanedComorbidity,
                        defaultComorbidity
                    ),
                    complication: mergeICDObject(
                        cleanedComplication,
                        defaultComplication
                    ),
                    other: mergeICDObject(cleanedOther, defaultOther),
                    externalCause: mergeICDObject(
                        cleanedExternalCause,
                        defaultExternalCause
                    )
                };
            };

            return processIcdCode(rawIcdCode);
        } else {
            const processIcdCode = (rawIcdCode?: diagnosisCode) => {
                const mergeFields = (defaultVal: code[], raw: code[]) =>
                    mergeArrays(defaultVal, raw);

                if (rawIcdCode) {
                    const convertRawCode = { ...rawIcdCode };
                    return {
                        principal: mergeFields(defaultPrincipal, [
                            convertRawCode.principal
                        ] as unknown as code[]),
                        comorbidity: mergeFields(
                            defaultComorbidity,
                            convertRawCode.comorbidity
                        ),
                        complication: mergeFields(
                            defaultComplication,
                            convertRawCode.complication
                        ),
                        other: mergeFields(defaultOther, convertRawCode.other),
                        externalCause: mergeFields(
                            defaultExternalCause,
                            convertRawCode.externalCause
                        )
                    };
                } else {
                    return {
                        principal: defaultPrincipal,
                        comorbidity: defaultComorbidity,
                        complication: defaultComplication,
                        other: defaultOther,
                        externalCause: defaultExternalCause
                    };
                }
            };
            return processIcdCode(rawIcdCode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        principal,
        comorbidity,
        complication,
        other,
        externalCause,
        rawIcdCode
    ]);

    const insertOrReplaceDiagnosis = (
        name:
            | "principal"
            | "comorbidity"
            | "complication"
            | "other"
            | "externalCause",
        // favItem: favoriteDiagnosis
        item: any
    ) => {
        delete item.counter;

        if (name === "principal") {
            // Replace
            encounterForm.setValue("diagnosis.principal", [
                {
                    id: uuidv4(),
                    ...item,
                    recorder: recorder.license,
                    recorderName: recorder.name
                }
            ]);
        } else {
            // Insert
            const prev = encounterForm.getValues(`diagnosis.${name}`);
            const exists = prev.some(
                (existingItem) => existingItem.term === item.term
            );
            if (!exists) {
                encounterForm.setValue(`diagnosis.${name}`, [
                    ...prev,
                    {
                        id: uuidv4(),
                        ...item,
                        recorder: recorder.license,
                        recorderName: recorder.name
                    }
                ]);
            }
        }
    };

    const removeDiagnosis = (
        name:
            | "principal"
            | "comorbidity"
            | "complication"
            | "other"
            | "externalCause",
        item: any
    ) => {
        const prev = encounterForm.getValues(`diagnosis.${name}`);
        const newList = prev.filter((temp) => temp.term !== item.term);
        encounterForm.setValue(`diagnosis.${name}`, newList);
    };

    const setDefaultOverwriteIcd10 = useCallback(() => {
        const defaultCoderForm = getDefaultCoderForm();
        encounterForm.setValue("overwriteIcd10", defaultCoderForm);
    }, [encounterForm, getDefaultCoderForm]);

    return (
        <EncounterContext.Provider
            value={{
                rawEncounterData,
                encounterForm,
                rawIcdCode: rawIcdCode ?? {
                    principal: [],
                    comorbidity: [],
                    complication: [],
                    other: [],
                    externalCause: []
                },
                // isIcdCodeChange,
                allDiagnosis: allDiagnosis.flat(),
                getDefaultCoderForm,
                submitDoctor,
                submitCoder,
                loading,
                fetchedEncounter,
                isOverride,
                isEditable,
                recorder,
                activeTab: activeTab as "coder" | "doctor",
                onChangeTab: handleOnChangeTab,
                activeUtilTab: activeUtilTab as "fav-dx" | "re-dx",
                onChangeUtilTab: handleOnChangeUtilTab,
                insertOrReplaceDiagnosis,
                removeDiagnosis,
                clinicData,
                setDefaultOverwriteIcd10,
                service: _serviceParams,
                impressApp,
                openConfirmCode,
                handleIsSure,
                handleCloseCode
            }}
        >
            {children}
        </EncounterContext.Provider>
    );
};

export default EncounterProvider;
